import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import * as PropTypes from 'prop-types';

import Modal from '@lwt-helix/modal';
import { Button } from '@lwt-helix/buttons';
import SecurityGroupSelect from './SecurityGroupSelect';
import InviteSelect from './InviteSelect';
import { createInvitations, fetchInvitations } from '../../../../api/AuthenticationApi';
import { clearingStore } from '../../../../actions/AuthActions';
import {
    EmailValidationNotification,
    SendInviteNotification
} from './Notifications';

const UsersModal = ({
    show,
    closeModal,
    options,
    permissions,
    securityGroups,
    clientId,
    error,
    response
}) => {
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [
        emailValidationNotification,
        setEmailValidationNotification
    ] = useState(null);
    const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
    const [sendInviteNotification, setSendInviteNotification] = useState(null);
    const [emailList, setEmailList] = useState([]);
    const [securityGroup, setSecurityGroup] = useState({});

    const [isSecurityGroupValid, setIsSecurityGroupValid] = useState(false);
    const dispatch = useDispatch();

    const sendInvite = () => {
        let roleId = '';
        securityGroups.forEach((element) => {
            if (Object.values(element).includes(securityGroup.value)) {
                roleId = element.id;
            }
        });

        const invites = [];
        emailList.forEach((element) => {
            if (Object.keys(element).length === 3) {
                const emailOnlyInvite = {
                    roleId: roleId,
                    connections: [{
                        appId:'bo',
                        name: clientId,
                        clientId: clientId
                    }],
                    emailAddress: element.label
                };
                invites.push(emailOnlyInvite);
            } else {
                const fullInvite = {
                    roleId: roleId,
                    connections: [{
                        appId:'bo',
                        ...(element.officeId) && { officeId: element.officeId },// only add this field if it exists
                        ...(element.id) && { memberId: element.id },// only add this field if it exists
                        name: clientId,
                        clientId: clientId
                        }],
                    firstName: element.firstName,
                    lastName: element.lastName,
                    emailAddress: element.primaryEmailAddress
                };

                invites.push(fullInvite);
            }
        });

        dispatch(createInvitations(invites));
    };

    useEffect(() => {
        setSendInviteNotification(
            response
                ? 'success'
                : error
                    ? 'fail'
                    : null
        );
    }, [error, response]);

    const resetSettings = () => {
        setIsEmailValid(false);
        sendInviteNotification === 'success' && dispatch(fetchInvitations());
        setEmailValidationNotification(null);
        setSendInviteNotification(null);
        dispatch(clearingStore());
        closeModal();
    };

    const handleSend = () => {
        return emailValidationNotification === 'notRosterMember' ? setConfirmationModalIsOpen(true) : sendInvite();
    };

    const confirmSend = () => {
        sendInvite();
        setConfirmationModalIsOpen(false);
    };

    const renderConfirmationButtons = (
        <>
            <Button
                outline={true}
                onClick={() => setConfirmationModalIsOpen(false)}
                className="mr-2"
                dataLwtId="confirmation-cancel-invite-button"
            >
                Cancel
            </Button>
            <Button
                color={'primary'}
                onClick={() => confirmSend()}
                dataLwtId="confirmation-send-invite-button"
            >
                Send Invite
            </Button>
        </>
    );

    const confirmationMessage = (
        <>
            <EmailValidationNotification notification={'sendConfirmation'} />
            <br />
            <div className="float-right">{renderConfirmationButtons}</div>
            <br />
        </>
    );

    const renderSendCancelButtons = (
        <>
            <Button
                outline={true}
                onClick={() => resetSettings()}
                className="mr-2"
                dataLwtId="cancel-invite-button"
            >
                Cancel
            </Button>
            <Button
                disabled={!isEmailValid || !isSecurityGroupValid}
                color={'primary'}
                onClick={() => handleSend()}
                dataLwtId="send-invite-button"
            >
                Send Invite
            </Button>
        </>
    );

    const renderCloseButton = (
        <Button
            outline={true}
            onClick={() => resetSettings()}
            dataLwtId="close-send-invite-modal-button"
        >
            Close
        </Button>
    );

    const renderModalButtons = () => {
        return sendInviteNotification === 'success'
            ? renderCloseButton
            : renderSendCancelButtons;
    };

    const message = (
        <>
            <div>Email Address</div>
            <InviteSelect
                setIsEmailValid={setIsEmailValid}
                setEmailValidationNotification={setEmailValidationNotification}
                setSendInviteNotification={setSendInviteNotification}
                setEmailList={setEmailList}
                options={options}
                permissions={permissions}
            />
            <EmailValidationNotification notification={emailValidationNotification} />
            <div>Security Group</div>
            <SecurityGroupSelect
                securityGroups={securityGroups}
                setSecurityGroup={setSecurityGroup}
                setIsSecurityGroupValid={setIsSecurityGroupValid}
            />
            <br />
            <div className="float-right">{renderModalButtons()}</div>
            <br />
            <SendInviteNotification notification={sendInviteNotification} />
        </>
    );

    return (
        <>
            <Modal
                size="lg"
                dataLwtId="invite-new-user-modal"
                title="Invite New User(s)"
                show={show}
                showFooter={false}
            >
                {message}
            </Modal>
            <Modal
                show={confirmationModalIsOpen}
                size="lg"
                dataLwtId="confirm-send-modal"
                title={<span className="text-warning">Warning</span>}
                showFooter={false}
            >
                {confirmationMessage}
            </Modal>
        </>
    );
};

UsersModal.propTypes = {
    show: PropTypes.bool,
    closeModal: PropTypes.func,
    options: PropTypes.array,
    permissions: PropTypes.object,
    securityGroups: PropTypes.array,
    clientId: PropTypes.string,
    error: PropTypes.object,
    response: PropTypes.array
};

export default UsersModal;